import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button, Spinner, Badge, Alert, Form, Row, Col } from "react-bootstrap";
import { formatDistance } from "date-fns";
import pt from "date-fns/locale/pt-BR";
import {
  IconUsers,
  IconPlus,
  IconEdit,
  IconTrash,
  IconShield,
  IconPhone,
  IconLock,
  IconSearch,
  IconFilter,
  IconUser,
  IconBuilding,
} from "@tabler/icons-react";
import Swal from "sweetalert2";
import { deleteUser, listUsers } from "./services/UserServicesV2";
import { getUserStatusBadge, SUPERUSER_LEVELS } from "./utils";
import { formatPhone } from "../Customers/utils";
import DatatablePaginated from "../../jsx/components/DatatablePaginated";
import UserModal from "./modal/UserModal";
import UserPermissionsModal from "./modal/UserPermissionsModal";
import { openSupportWhatsApp } from "../../jsx/components/Auth/ProtectedRoute";
import "./UserList.css";
import "./UserList.mobile.css";

const UserList = ({
  // Props de limitação de uso (injetadas pelo ProtectedRoute)
  isLimitExceeded = false,
  usageInfo = null,
  feature = null,
  limitedActions = [],
  onUpgradeClick = null,
}) => {
  // Verificar quais ações estão limitadas
  const canCreate = !limitedActions.includes("create");
  const canUpdate = !limitedActions.includes("update");
  const canDelete = !limitedActions.includes("delete");

  // Estados principais
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);

  // Estados dos modais
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(15);

  // Estados de filtros
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    is_superuser: "",
    superuser_level: "",
    is_active: "",
    page: 1,
    page_size: 15,
    ordering: "-id",
  });

  useEffect(() => {
    fetchUsers();
  }, [filters]);

  // Reagir às mudanças de filtros
  useEffect(() => {
    const newFilters = { ...filters, page: 1 };

    if (filterStatus === "active") {
      newFilters.is_active = "1";
    } else if (filterStatus === "inactive") {
      newFilters.is_active = "0";
    } else {
      newFilters.is_active = "";
    }

    if (searchTerm !== filters.search) {
      newFilters.search = searchTerm;
    }

    setFilters(newFilters);
  }, [filterStatus, searchTerm]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await listUsers(filters);
      const data = response.data;

      // Suporta resposta paginada (com results) e array direto (legado)
      if (data.results !== undefined) {
        setUsers(data.results);
        setTotalRows(data.count || 0);
        setCurrentPage(data.current_page || filters.page);
      } else if (Array.isArray(data)) {
        setUsers(data);
        setTotalRows(data.length);
        setCurrentPage(1);
      } else {
        setUsers([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
      Swal.fire({
        title: "Erro",
        text: "Não foi possível carregar a lista de usuários",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = useCallback((page) => {
    setFilters((prev) => ({
      ...prev,
      page: page,
    }));
  }, []);

  const handleCreateUser = () => {
    if (!canCreate) {
      openSupportWhatsApp(feature?.name || "Usuários");
      return;
    }
    setSelectedUser(null);
    setIsEditMode(false);
    setShowUserModal(true);
  };

  const handleEditUser = useCallback(
    (user, e) => {
      if (e) e.stopPropagation();
      if (!canUpdate) {
        openSupportWhatsApp(feature?.name || "Usuários");
        return;
      }
      setSelectedUser(user);
      setIsEditMode(true);
      setShowUserModal(true);
    },
    [canUpdate, feature]
  );

  const handleViewPermissions = useCallback((user, e) => {
    if (e) e.stopPropagation();
    setSelectedUser(user);
    setShowPermissionsModal(true);
  }, []);

  const handleDeleteUser = useCallback(
    async (user, e) => {
      if (e) e.stopPropagation();
      if (!canDelete) {
        openSupportWhatsApp(feature?.name || "Usuários");
        return;
      }

      const result = await Swal.fire({
        title: "Tem certeza?",
        text: `Deseja inativar o usuário "${user.first_name} ${user.last_name}"? Esta ação pode ser revertida.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Sim, inativar!",
        cancelButtonText: "Cancelar",
      });

      if (result.isConfirmed) {
        try {
          await deleteUser(user.id);
          Swal.fire({
            title: "Sucesso!",
            text: "Usuário inativado com sucesso!",
            icon: "success",
          });
          fetchUsers();
        } catch (error) {
          Swal.fire({
            title: "Erro!",
            text: "Erro ao inativar usuário",
            icon: "error",
          });
        }
      }
    },
    [canDelete, feature]
  );

  const getLevelClass = (level) => {
    switch (level) {
      case 1:
        return "admin";
      case 2:
        return "manager";
      case 3:
        return "supervisor";
      case 4:
        return "attendant";
      default:
        return "user";
    }
  };

  const getLevelLabel = (level) => {
    return SUPERUSER_LEVELS[level] || "Usuário";
  };

  const getStats = () => {
    const total = users.length;
    const active = users.filter((u) => u && u.is_active).length;
    const inactive = total - active;
    const superusers = users.filter((u) => u && u.superuser_level > 0).length;

    return { total, active, inactive, superusers };
  };

  const stats = getStats();

  // Configuração para filtros de busca
  const filterByField = {
    fields: ["first_name", "email", "phone"],
    labels: ["Nome", "Email", "Telefone"],
  };

  // Definir as colunas da tabela
  const columns = useMemo(
    () => [
      {
        name: "Usuário",
        selector: (row) => row.first_name,
        sortable: true,
        cell: (row) => (
          <div className="usr-user-info">
            {row.photo && (
              <img
                src={row.photo}
                alt={row.first_name}
                className="usr-user-avatar"
              />
            )}
            <div className="usr-user-details">
              <span className="usr-user-name">
                {row.first_name} {row.last_name}
              </span>
              <span className="usr-user-email">{row.email}</span>
            </div>
          </div>
        ),
        grow: 2,
        minWidth: "200px",
      },
      {
        name: "Contato",
        selector: (row) => row.phone,
        sortable: true,
        cell: (row) => (
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <IconPhone size={14} className="text-muted" />
            <span style={{ fontSize: "13px" }}>{formatPhone(row.phone)}</span>
          </div>
        ),
        width: "140px",
      },
      {
        name: "Departamento",
        selector: (row) => row.department_name,
        sortable: true,
        cell: (row) => (
          <span>
            {row.department_name ? (
              <span className="usr-department-badge">
                <IconBuilding size={12} className="me-1" />
                {row.department_name}
              </span>
            ) : (
              <span style={{ color: "#6c757d", fontSize: "12px" }}>-</span>
            )}
          </span>
        ),
        width: "140px",
      },
      {
        name: "Nível",
        selector: (row) => row.superuser_level,
        sortable: true,
        cell: (row) => (
          <span
            className={`usr-level-badge ${getLevelClass(row.superuser_level)}`}
          >
            {getLevelLabel(row.superuser_level)}
          </span>
        ),
        width: "120px",
      },
      {
        name: "Status",
        selector: (row) => row.is_active,
        sortable: true,
        cell: (row) => (
          <span
            className={`usr-status-badge ${row.is_active ? "active" : "inactive"}`}
          >
            {row.is_active ? "Ativo" : "Inativo"}
          </span>
        ),
        width: "100px",
      },
      {
        name: "Último Login",
        selector: (row) => row.last_login?.created_at,
        sortable: true,
        cell: (row) => (
          <span style={{ fontSize: "12px", color: "#6c757d" }}>
            {row.last_login?.created_at
              ? formatDistance(new Date(row.last_login.created_at), new Date(), {
                  addSuffix: true,
                  locale: pt,
                })
              : "Nunca"}
          </span>
        ),
        width: "130px",
      },
      {
        name: "Ações",
        cell: (row) => (
          <div
            style={{ display: "flex", gap: "4px", justifyContent: "flex-end" }}
          >
            <Button
              variant="primary"
              size="sm"
              onClick={(e) => handleEditUser(row, e)}
              title="Editar"
              style={{ padding: "4px 6px" }}
              disabled={!canUpdate}
            >
              {!canUpdate ? <IconLock size={14} /> : <IconEdit size={14} />}
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => handleViewPermissions(row, e)}
              title="Gerenciar permissões"
              style={{ padding: "4px 6px" }}
            >
              <IconShield size={14} />
            </Button>

            <Button
              variant="danger"
              size="sm"
              onClick={(e) => handleDeleteUser(row, e)}
              title="Inativar"
              style={{ padding: "4px 6px" }}
              disabled={!canDelete}
            >
              {!canDelete ? <IconLock size={14} /> : <IconTrash size={14} />}
            </Button>
          </div>
        ),
        width: "130px",
        ignoreRowClick: true,
      },
    ],
    [handleEditUser, handleViewPermissions, handleDeleteUser, canUpdate, canDelete]
  );

  if (loading && users.length === 0) {
    return (
      <div className="usr-container">
        <div className="usr-loading-container">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Carregando usuários...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="usr-container">
      <div className="usr-header">
        <div className="usr-header-content">
          <h1 className="usr-title">
            <span className="usr-title-icon">
              <IconUsers size={24} />
            </span>
            Usuários
            {totalRows > 0 && (
              <Badge bg="primary" className="usr-title-badge">
                {totalRows}
              </Badge>
            )}
          </h1>
        </div>
      </div>

      {/* Alert de limitação de uso */}
      {isLimitExceeded && (
        <Alert variant="warning" className="usr-info-card border-0 shadow-sm">
          <div className="d-flex align-items-center">
            <IconLock size={20} className="text-warning me-2" />
            <div className="flex-grow-1">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small>
                  <strong>Modo Limitado - Gerenciamento de Usuários</strong>
                </small>
                <span className="badge bg-warning text-dark">
                  {usageInfo?.current_usage}/{usageInfo?.usage_limit}
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  Você pode visualizar usuários, mas ações de{" "}
                  {limitedActions
                    .map((action) =>
                      action === "create"
                        ? "criação"
                        : action === "update"
                          ? "edição"
                          : action === "delete"
                            ? "exclusão"
                            : action
                    )
                    .join(", ")}{" "}
                  estão limitadas
                </small>
                <Button
                  size="sm"
                  variant="warning"
                  onClick={() =>
                    onUpgradeClick?.() ||
                    openSupportWhatsApp(feature?.name || "Usuários")
                  }
                  style={{ fontSize: "12px", padding: "2px 8px" }}
                >
                  Aumentar Limite
                </Button>
              </div>
            </div>
          </div>
        </Alert>
      )}

      <div className="usr-search-container">
        <span className="usr-search-icon">
          <IconSearch size={18} />
        </span>
        <input
          type="text"
          className="usr-search-input"
          placeholder="Buscar por nome, email ou telefone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="usr-stats-container usr-stats-mobile">
        <div className="usr-stat-card">
          <div className="usr-stat-number">{totalRows}</div>
          <div className="usr-stat-label">Total</div>
        </div>
        <div className="usr-stat-card">
          <div className="usr-stat-number">{stats.active}</div>
          <div className="usr-stat-label">Ativos</div>
        </div>
        <div className="usr-stat-card">
          <div className="usr-stat-number">{stats.inactive}</div>
          <div className="usr-stat-label">Inativos</div>
        </div>
        <div className="usr-stat-card">
          <div className="usr-stat-number">{stats.superusers}</div>
          <div className="usr-stat-label">Admins</div>
        </div>
      </div>

      {/* DataTable wrapper responsivo */}
      <div className="usr-mobile-container">
        <div className="usr-datatable-wrapper">
          <div
            style={{
              marginBottom: "20px",
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <Button
                variant={filterStatus === "all" ? "primary" : "outline-secondary"}
                size="sm"
                onClick={() => setFilterStatus("all")}
              >
                <IconFilter size={16} className="me-1" />
                Todos
              </Button>
              <Button
                variant={filterStatus === "active" ? "success" : "outline-success"}
                size="sm"
                onClick={() => setFilterStatus("active")}
              >
                Ativos
              </Button>
              <Button
                variant={
                  filterStatus === "inactive" ? "warning" : "outline-warning"
                }
                size="sm"
                onClick={() => setFilterStatus("inactive")}
              >
                Inativos
              </Button>
            </div>
            <Button
              variant={!canCreate ? "outline-warning" : "primary"}
              size="sm"
              onClick={handleCreateUser}
              className="usr-add-button-desktop"
            >
              {!canCreate ? (
                <IconLock size={16} className="me-1" />
              ) : (
                <IconPlus size={16} className="me-1" />
              )}
              {!canCreate ? "Solicitar Limite" : "Novo Usuário"}
            </Button>
          </div>

          {users.length === 0 && !loading ? (
            <div className="usr-empty-state">
              <div className="usr-empty-icon">
                <IconUsers size={48} />
              </div>
              <h3>
                {searchTerm
                  ? "Nenhum usuário encontrado"
                  : "Nenhum usuário cadastrado"}
              </h3>
              <p>
                {searchTerm
                  ? "Nenhum usuário corresponde aos filtros selecionados."
                  : "Comece criando seu primeiro usuário!"}
              </p>
              {!searchTerm && (
                <Button
                  variant={!canCreate ? "outline-warning" : "primary"}
                  onClick={handleCreateUser}
                >
                  {!canCreate ? (
                    <IconLock size={18} className="me-1" />
                  ) : (
                    <IconPlus size={18} className="me-1" />
                  )}
                  {!canCreate ? "Solicitar Limite" : "Criar Primeiro Usuário"}
                </Button>
              )}
            </div>
          ) : (
            <DatatablePaginated
              componentName="user-list"
              data={users}
              columns={columns}
              totalRows={totalRows}
              handlePageChange={handlePageChange}
              pagination={true}
              paginationServer={true}
              paginationTotalRows={totalRows}
              paginationPerPage={filters.page_size}
              paginationDefaultPage={filters.page}
              isLoading={loading}
              selectable={false}
              filterByField={filterByField}
              classes="px-0"
            />
          )}
        </div>

        {/* Mobile Cards */}
        <div className="usr-mobile-cards">
          {loading ? (
            <div className="usr-mobile-loader">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Carregando...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="usr-mobile-empty">
              <div className="usr-empty-icon">
                <IconUsers size={48} />
              </div>
              <h3>
                {searchTerm
                  ? "Nenhum usuário encontrado"
                  : "Nenhum usuário cadastrado"}
              </h3>
              <p>
                {searchTerm
                  ? "Nenhum usuário corresponde aos filtros selecionados."
                  : "Comece criando seu primeiro usuário!"}
              </p>
              {!searchTerm && (
                <Button
                  variant={!canCreate ? "outline-warning" : "primary"}
                  onClick={handleCreateUser}
                >
                  {!canCreate ? (
                    <IconLock size={18} className="me-1" />
                  ) : (
                    <IconPlus size={18} className="me-1" />
                  )}
                  {!canCreate ? "Solicitar Limite" : "Criar Primeiro Usuário"}
                </Button>
              )}
            </div>
          ) : (
            <>
              {users.map((user) => (
                <div key={user.id} className="usr-card">
                  {/* Header do card */}
                  <div className="usr-card-header">
                    <div className="usr-card-user-info">
                      {user.photo && (
                        <img
                          src={user.photo}
                          alt={user.first_name}
                          className="usr-card-avatar"
                        />
                      )}
                      <div className="usr-card-user-details">
                        <h4 className="usr-card-user-name">
                          {user.first_name} {user.last_name}
                        </h4>
                        <p className="usr-card-user-email">{user.email}</p>
                      </div>
                    </div>
                    <span
                      className={`usr-card-status-badge ${user.is_active ? "active" : "inactive"}`}
                    >
                      {user.is_active ? "Ativo" : "Inativo"}
                    </span>
                  </div>

                  {/* Body do card */}
                  <div className="usr-card-body">
                    <div className="usr-card-info-row">
                      <span className="usr-card-info-label">Telefone:</span>
                      <span className="usr-card-info-value">
                        <div className="usr-card-phone">
                          <IconPhone size={14} />
                          {formatPhone(user.phone)}
                        </div>
                      </span>
                    </div>
                    <div className="usr-card-info-row">
                      <span className="usr-card-info-label">Departamento:</span>
                      <span className="usr-card-info-value">
                        {user.department_name ? (
                          <span className="usr-card-department">
                            {user.department_name}
                          </span>
                        ) : (
                          "-"
                        )}
                      </span>
                    </div>
                    <div className="usr-card-info-row">
                      <span className="usr-card-info-label">Nível:</span>
                      <span className="usr-card-info-value">
                        <span
                          className={`usr-card-level ${getLevelClass(user.superuser_level)}`}
                        >
                          {getLevelLabel(user.superuser_level)}
                        </span>
                      </span>
                    </div>
                    <div className="usr-card-info-row">
                      <span className="usr-card-info-label">Último Login:</span>
                      <span className="usr-card-info-value usr-card-last-login">
                        {user.last_login?.created_at
                          ? formatDistance(
                              new Date(user.last_login.created_at),
                              new Date(),
                              {
                                addSuffix: true,
                                locale: pt,
                              }
                            )
                          : "Nunca"}
                      </span>
                    </div>
                  </div>

                  {/* Actions do card */}
                  <div className="usr-card-actions">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={(e) => handleEditUser(user, e)}
                      className="usr-action-btn-mobile"
                      disabled={!canUpdate}
                    >
                      {!canUpdate ? (
                        <IconLock size={14} />
                      ) : (
                        <IconEdit size={14} />
                      )}
                      Editar
                    </Button>

                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={(e) => handleViewPermissions(user, e)}
                      className="usr-action-btn-mobile"
                    >
                      <IconShield size={14} />
                      Permissões
                    </Button>

                    <Button
                      size="sm"
                      variant="danger"
                      onClick={(e) => handleDeleteUser(user, e)}
                      className="usr-action-btn-mobile"
                      disabled={!canDelete}
                    >
                      {!canDelete ? (
                        <IconLock size={14} />
                      ) : (
                        <IconTrash size={14} />
                      )}
                      Inativar
                    </Button>
                  </div>
                </div>
              ))}

              {/* Paginação mobile */}
              <div className="usr-mobile-pagination">
                <div className="usr-pagination-info">
                  Página {currentPage} de {Math.ceil(totalRows / perPage)}
                </div>
                <div className="usr-pagination-buttons">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="usr-pagination-btn"
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={currentPage >= Math.ceil(totalRows / perPage)}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="usr-pagination-btn"
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <button
        className="usr-floating-button"
        onClick={handleCreateUser}
        title="Novo Usuário"
      >
        <IconPlus size={24} />
      </button>

      {/* Modal de Cadastro/Edição */}
      <UserModal
        show={showUserModal}
        onHide={() => setShowUserModal(false)}
        user={selectedUser}
        isEdit={isEditMode}
        onSave={fetchUsers}
      />

      {/* Modal de Permissões */}
      <UserPermissionsModal
        show={showPermissionsModal}
        onHide={() => setShowPermissionsModal(false)}
        user={selectedUser}
        onSave={fetchUsers}
      />
    </div>
  );
};

export default UserList;
